import logger from '../lib/logger';
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Phone, MessageCircle, FileText, Calendar, Search,
  AlertTriangle, ArrowRight, Clock, User, PhoneCall,
  ChevronRight, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';

const ACTIVITY_ICONS = {
  call: { icon: PhoneCall, color: 'text-green-600', bg: 'bg-green-50' },
  whatsapp: { icon: MessageCircle, color: 'text-green-500', bg: 'bg-green-50' },
  note_added: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  note: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  status_change: { icon: ArrowRight, color: 'text-amber-600', bg: 'bg-amber-50' },
  assigned: { icon: User, color: 'text-violet-600', bg: 'bg-violet-50' },
  site_visit: { icon: Calendar, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  deal: { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  default: { icon: Clock, color: 'text-slate-500', bg: 'bg-slate-100' },
};

const Communications = () => {
  const [feed, setFeed] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [staleContacts, setStaleContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactThread, setContactThread] = useState([]);
  const [isThreadOpen, setIsThreadOpen] = useState(false);
  const [activeView, setActiveView] = useState('feed');
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterDays, setFilterDays] = useState('7');
  const [searchTerm, setSearchTerm] = useState('');
  const [agents, setAgents] = useState([]);
  const [filterAgent, setFilterAgent] = useState('all');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchFeed(); }, [filterType, filterSource, filterDays, filterAgent]);

  const fetchAll = async () => {
    try {
      const [contactsRes, staleRes, agentsRes] = await Promise.all([
        api.get('/communications/contacts'),
        api.get('/communications/stale?hours=48'),
        api.get('/team/agents').catch(() => ({ data: [] })),
      ]);
      setContacts(contactsRes.data);
      setStaleContacts(staleRes.data);
      setAgents(agentsRes.data);
    } catch (e) { logger.error(e); }
    fetchFeed();
  };

  const fetchFeed = async () => {
    try {
      let url = `/communications/feed?days=${filterDays}`;
      if (filterType !== 'all') url += `&activity_type=${filterType}`;
      if (filterSource !== 'all') url += `&source=${filterSource}`;
      if (filterAgent !== 'all') url += `&agent_id=${filterAgent}`;
      const res = await api.get(url);
      setFeed(res.data);
    } catch (e) { logger.error(e); }
  };

  const openThread = async (contact) => {
    setSelectedContact(contact);
    setIsThreadOpen(true);
    try {
      const res = await api.get(`/leads/${contact.id}/activities`);
      setContactThread(res.data);
    } catch { setContactThread([]); }
  };

  const handleCall = (contact) => {
    window.location.href = `tel:${contact.phone}`;
    if (contact.id) api.post(`/leads/${contact.id}/log-call`).then(() => { fetchAll(); }).catch(() => {});
  };

  const handleWhatsApp = (contact) => {
    const phone = contact.phone?.replace(/\D/g, '');
    const name = contact.name || '';
    const msg = encodeURIComponent(`Hi ${name}, following up regarding your property inquiry. - Aavaas Consultants`);
    const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const waUrl = isMobile ? `https://wa.me/${phone}?text=${msg}` : `https://web.whatsapp.com/send?phone=${phone}&text=${msg}`;
    const a = document.createElement('a'); a.href = waUrl; a.target = '_blank'; a.rel = 'noopener noreferrer'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
    if (contact.id) api.post(`/leads/${contact.id}/log-whatsapp`).then(() => { fetchAll(); }).catch(() => {});
  };

  const handleAddNote = async (contactId) => {
    const note = window.prompt('Add note:');
    if (!note) return;
    try {
      await api.post(`/leads/${contactId}/notes?content=${encodeURIComponent(note)}`);
      toast.success('Note added');
      fetchAll();
      if (selectedContact?.id === contactId) {
        const res = await api.get(`/leads/${contactId}/activities`);
        setContactThread(res.data);
      }
    } catch { toast.error('Failed'); }
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'never';
    const h = Math.floor((Date.now() - new Date(dateStr)) / (1000 * 60 * 60));
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  const getSilenceLabel = (hours) => {
    if (hours === null || hours === undefined) return { text: 'Never contacted', cls: 'text-red-600 font-semibold' };
    if (hours < 24) return { text: `${hours}h ago`, cls: 'text-green-600' };
    if (hours < 48) return { text: `${Math.floor(hours / 24)}d ago`, cls: 'text-amber-600' };
    return { text: `${Math.floor(hours / 24)}d ago`, cls: 'text-red-600 font-semibold' };
  };

  const formatBudget = (b) => {
    if (!b) return '';
    const n = typeof b === 'string' ? parseFloat(b) : b;
    if (isNaN(n)) return `₹${b}`;
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(0)}L`;
    return `₹${n.toLocaleString('en-IN')}`;
  };

  const filteredFeed = searchTerm
    ? feed.filter(a => a.lead_name?.toLowerCase().includes(searchTerm.toLowerCase()) || a.lead_phone?.includes(searchTerm))
    : feed;

  const filteredContacts = searchTerm
    ? contacts.filter(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone?.includes(searchTerm))
    : contacts;

  const highValueStale = staleContacts.filter(c => c.category === 'HOT' || ['negotiation', 'site_visit_scheduled'].includes(c.status));
  const getActivityStyle = (type) => ACTIVITY_ICONS[type] || ACTIVITY_ICONS.default;

  return (
    <Layout>
      <div className="aavaas-page" data-testid="communications-page">
        {/* Header */}
        <div className="mb-5">
          <h1 className="aavaas-page-title" data-testid="communications-title">Communications</h1>
          <p className="text-slate-500 text-xs mt-0.5">Track all interactions in one place</p>
        </div>

        {/* Stale Alert */}
        {staleContacts.length > 0 && (
          <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5" data-testid="stale-alert">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm text-red-700 font-medium">{staleContacts.length} leads with no contact in 48+ hours</span>
              {highValueStale.length > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{highValueStale.length} high-value</span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex bg-slate-200/70 rounded-lg p-0.5">
            <button
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${activeView === 'feed' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveView('feed')}
              data-testid="view-feed"
            >
              Activity Feed
            </button>
            <button
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${activeView === 'contacts' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveView('contacts')}
              data-testid="view-contacts"
            >
              Contacts
            </button>
          </div>
          <span className="text-xs text-slate-400 ml-auto">
            {activeView === 'feed' ? `${filteredFeed.length} activities` : `${filteredContacts.length} contacts`}
          </span>
        </div>

        {/* Search + Filters */}
        <div className="mb-4 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or phone..."
              className="pl-10 bg-white border border-slate-200 h-9 text-sm rounded-lg shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="comm-search"
            />
          </div>
          {activeView === 'feed' && (
            <div className="flex flex-wrap gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[120px] h-8 text-xs bg-white border border-slate-200 shadow-sm" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="call">Calls</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="note_added">Notes</SelectItem>
                  <SelectItem value="status_change">Status Changes</SelectItem>
                  <SelectItem value="deal">Deals</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="w-[115px] h-8 text-xs bg-white border border-slate-200 shadow-sm" data-testid="filter-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="99acres">99acres</SelectItem>
                  <SelectItem value="magicbricks">MagicBricks</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterDays} onValueChange={setFilterDays}>
                <SelectTrigger className="w-[100px] h-8 text-xs bg-white border border-slate-200 shadow-sm" data-testid="filter-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Today</SelectItem>
                  <SelectItem value="3">3 Days</SelectItem>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && agents.length > 0 && (
                <Select value={filterAgent} onValueChange={setFilterAgent}>
                  <SelectTrigger className="w-[120px] h-8 text-xs bg-white border border-slate-200 shadow-sm" data-testid="filter-agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* Activity Feed View */}
        {activeView === 'feed' && (
          <div className="space-y-2">
            {filteredFeed.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-100 shadow-sm py-16 text-center" data-testid="empty-feed">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-500">No activities found</p>
                <p className="text-xs text-slate-400 mt-1">Try adjusting your filters</p>
              </div>
            ) : filteredFeed.map(activity => {
              const style = getActivityStyle(activity.activity_type);
              const Icon = style.icon;
              return (
                <div
                  key={activity.id}
                  className="bg-white rounded-lg border border-slate-100 shadow-sm p-3.5 hover:border-slate-200 transition-all cursor-pointer"
                  onClick={() => openThread({ id: activity.lead_id, name: activity.lead_name, phone: activity.lead_phone, source: activity.lead_source, status: activity.lead_status, budget: activity.lead_budget, category: activity.lead_category })}
                  data-testid={`activity-${activity.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${style.bg}`}>
                      <Icon className={`w-4 h-4 ${style.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Name + Tags row */}
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className="font-semibold text-sm text-slate-900">{activity.lead_name || 'Unknown'}</span>
                        {activity.lead_source && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-none ${activity.lead_source === '99acres' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                            {activity.lead_source}
                          </span>
                        )}
                        {activity.lead_category === 'HOT' && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-none bg-red-50 text-red-600">HOT</span>
                        )}
                      </div>
                      {/* Description */}
                      <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">{activity.description}</p>
                      {/* Meta row */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] text-slate-400">{getTimeAgo(activity.created_at)}</span>
                        {activity.agent_name && (
                          <>
                            <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                            <span className="text-[11px] text-slate-400">{activity.agent_name}</span>
                          </>
                        )}
                        {activity.lead_phone && (
                          <>
                            <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                            <span className="text-[11px] text-slate-400">{activity.lead_phone}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Action buttons - hidden on mobile, visible on desktop */}
                    <div className="hidden lg:flex gap-1 flex-shrink-0 items-start" onClick={(e) => e.stopPropagation()}>
                      {activity.lead_phone && (
                        <>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-green-50" onClick={() => handleCall({ id: activity.lead_id, phone: activity.lead_phone })} data-testid={`activity-call-${activity.id}`}>
                            <Phone className="w-3.5 h-3.5 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-green-50" onClick={() => handleWhatsApp({ id: activity.lead_id, phone: activity.lead_phone, name: activity.lead_name })} data-testid={`activity-wa-${activity.id}`}>
                            <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-blue-50" onClick={() => handleAddNote(activity.lead_id)} data-testid={`activity-note-${activity.id}`}>
                        <FileText className="w-3.5 h-3.5 text-blue-400" />
                      </Button>
                    </div>
                  </div>
                  {/* Mobile action bar */}
                  <div className="flex lg:hidden items-center gap-1 mt-2.5 pt-2.5 border-t border-slate-50" onClick={(e) => e.stopPropagation()}>
                    {activity.lead_phone && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1 gap-1 border-slate-200" onClick={() => handleCall({ id: activity.lead_id, phone: activity.lead_phone })}>
                          <Phone className="w-3 h-3 text-green-600" /> Call
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1 gap-1 border-slate-200" onClick={() => handleWhatsApp({ id: activity.lead_id, phone: activity.lead_phone, name: activity.lead_name })}>
                          <MessageCircle className="w-3 h-3 text-green-500" /> WhatsApp
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-1 gap-1 border-slate-200" onClick={() => handleAddNote(activity.lead_id)}>
                      <FileText className="w-3 h-3 text-blue-400" /> Note
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contacts View */}
        {activeView === 'contacts' && (
          <div className="space-y-2">
            {filteredContacts.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-100 shadow-sm py-16 text-center" data-testid="empty-contacts">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <User className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-500">No contacts found</p>
                <p className="text-xs text-slate-400 mt-1">Add leads to see them here</p>
              </div>
            ) : filteredContacts.map(contact => {
              const silence = getSilenceLabel(contact.silence_hours);
              const isHighValue = contact.category === 'HOT' || ['negotiation'].includes(contact.status);
              const isDanger = (contact.silence_hours === null || contact.silence_hours > 48) && !['closed', 'converted', 'lost'].includes(contact.status);
              return (
                <div
                  key={contact.id}
                  className={`bg-white rounded-lg border shadow-sm overflow-hidden transition-all hover:border-slate-200 cursor-pointer ${isDanger ? 'border-l-[3px] border-l-red-400 border-red-200' : isHighValue ? 'border-l-[3px] border-l-amber-400 border-amber-200' : 'border-slate-100'}`}
                  onClick={() => openThread(contact)}
                  data-testid={`contact-${contact.id}`}
                >
                  <div className="p-3.5">
                    {/* Row 1: Name + Tags */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                      <span className="font-semibold text-sm text-slate-900">{contact.name}</span>
                      {contact.source && (
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-none ${contact.source === '99acres' ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                          {contact.source}
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-none capitalize ${contact.status === 'new' ? 'bg-blue-50 text-blue-600' : contact.status === 'negotiation' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                        {contact.status}
                      </span>
                      {contact.category === 'HOT' && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium leading-none bg-red-50 text-red-600">HOT</span>
                      )}
                    </div>

                    {/* Row 2: Phone + Budget + Silence */}
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                      <span className="font-mono">{contact.phone}</span>
                      {contact.budget && (
                        <>
                          <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                          <span className="font-medium text-slate-700">{formatBudget(contact.budget)}</span>
                        </>
                      )}
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                      <span className={silence.cls}>Last: {silence.text}</span>
                    </div>

                    {/* Row 3: Activity counts + Actions */}
                    <div className="flex items-center justify-between">
                      {/* Counts */}
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="Calls">
                          <PhoneCall className="w-3.5 h-3.5 text-green-500" />
                          <span>{contact.calls}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="WhatsApp">
                          <MessageCircle className="w-3.5 h-3.5 text-green-400" />
                          <span>{contact.whatsapp}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="Notes">
                          <FileText className="w-3.5 h-3.5 text-blue-400" />
                          <span>{contact.notes}</span>
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-green-50" onClick={() => handleCall(contact)} data-testid={`contact-call-${contact.id}`}>
                          <Phone className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-green-50" onClick={() => handleWhatsApp(contact)} data-testid={`contact-wa-${contact.id}`}>
                          <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-blue-50" onClick={() => handleAddNote(contact.id)} data-testid={`contact-note-${contact.id}`}>
                          <FileText className="w-3.5 h-3.5 text-blue-400" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-slate-300 ml-1 hidden lg:block" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Conversation Thread Sheet */}
        <Sheet open={isThreadOpen} onOpenChange={setIsThreadOpen}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedContact && (
              <>
                <SheetHeader className="mb-4">
                  <SheetTitle className="text-lg">{selectedContact.name}</SheetTitle>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <a href={`tel:${selectedContact.phone}`} className="text-sm text-blue-600 font-mono">{selectedContact.phone}</a>
                    {selectedContact.source && <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">{selectedContact.source}</span>}
                    {selectedContact.category === 'HOT' && <span className="px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-600 font-medium">HOT</span>}
                  </div>
                </SheetHeader>

                {/* Quick Action Bar */}
                <div className="flex gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                  <Button size="sm" variant="outline" className="flex-1 text-xs h-9" onClick={() => handleCall(selectedContact)} data-testid="thread-call-btn">
                    <Phone className="w-3.5 h-3.5 mr-1.5 text-green-600" /> Call
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs h-9" onClick={() => handleWhatsApp(selectedContact)} data-testid="thread-wa-btn">
                    <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-green-500" /> WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs h-9" onClick={() => handleAddNote(selectedContact.id)} data-testid="thread-note-btn">
                    <FileText className="w-3.5 h-3.5 mr-1.5 text-blue-400" /> Note
                  </Button>
                </div>

                {/* Response Tracking */}
                {selectedContact.silence_hours !== undefined && (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-[11px] text-slate-500 mb-1">Last Contact</p>
                      <p className={`text-sm font-bold ${getSilenceLabel(selectedContact.silence_hours).cls}`}>
                        {getSilenceLabel(selectedContact.silence_hours).text}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-[11px] text-slate-500 mb-1">Activities</p>
                      <p className="text-sm font-bold text-slate-800">{selectedContact.total_activities || contactThread.length}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-[11px] text-slate-500 mb-1">Calls Made</p>
                      <p className="text-sm font-bold text-green-600">{selectedContact.calls || 0}</p>
                    </div>
                  </div>
                )}

                {/* Thread Timeline */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Timeline</h3>
                  {contactThread.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                        <Clock className="w-6 h-6 text-slate-300" />
                      </div>
                      <p className="text-sm text-slate-400">No activity recorded yet</p>
                    </div>
                  ) : contactThread.map((act, i) => {
                    const style = getActivityStyle(act.activity_type);
                    const Icon = style.icon;
                    return (
                      <div key={act.id || i} className="flex gap-3 relative">
                        {/* Timeline line */}
                        {i < contactThread.length - 1 && (
                          <div className="absolute left-[15px] top-8 w-px bottom-0 bg-slate-200" />
                        )}
                        <div className={`p-1.5 rounded-lg flex-shrink-0 z-10 ${style.bg}`}>
                          <Icon className={`w-3.5 h-3.5 ${style.color}`} />
                        </div>
                        <div className="flex-1 min-w-0 pb-4">
                          <p className="text-xs text-slate-700 leading-relaxed">{act.description}</p>
                          <p className="text-[11px] text-slate-400 mt-1">{getTimeAgo(act.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
};

export default Communications;
